// lib/leasing/planGeometry.ts
// A floor plan's unit outlines and which ones touch, pure so the unit brief (server) and the Launch
// bar's mini plan (browser) read neighbours the same way.
export type PlanPoint = { x: number; y: number };

/** How close two outlines must come, in plan widths, to count as touching (about 1.5% of the plan). */
export const NEIGHBOUR_GAP = 0.015;

export const readPolygon = (geoJson: unknown): PlanPoint[] => {
  const coords = (geoJson as { coordinates?: unknown } | null)?.coordinates;
  if (!Array.isArray(coords)) return [];
  return coords.filter((p): p is PlanPoint => typeof p?.x === 'number' && typeof p?.y === 'number');
};

const segmentDistance = (a: PlanPoint, b: PlanPoint, c: PlanPoint, d: PlanPoint): number => {
  // Min distance between segments ab and cd: the endpoints' distances to the other segment cover
  // every non-crossing case; a crossing is distance zero.
  const cross = (p: PlanPoint, q: PlanPoint, r: PlanPoint) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const d1 = cross(c, d, a), d2 = cross(c, d, b), d3 = cross(a, b, c), d4 = cross(a, b, d);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return 0;
  const pointToSegment = (p: PlanPoint, s: PlanPoint, e: PlanPoint) => {
    const dx = e.x - s.x, dy = e.y - s.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - s.x) * dx + (p.y - s.y) * dy) / len2));
    return Math.hypot(p.x - (s.x + t * dx), p.y - (s.y + t * dy));
  };
  return Math.min(pointToSegment(a, c, d), pointToSegment(b, c, d), pointToSegment(c, a, b), pointToSegment(d, a, b));
};

/** The smallest distance between two outlines, in plan units; Infinity when either is not drawn. */
export const outlineGap = (left: PlanPoint[], right: PlanPoint[]): number => {
  if (left.length < 2 || right.length < 2) return Infinity;
  let best = Infinity;
  for (let i = 0; i < left.length; i++) {
    const a = left[i]!, b = left[(i + 1) % left.length]!;
    for (let j = 0; j < right.length; j++) {
      const c = right[j]!, d = right[(j + 1) % right.length]!;
      best = Math.min(best, segmentDistance(a, b, c, d));
      if (best === 0) return 0;
    }
  }
  return best;
};

/** Units on the same floor whose outlines touch, or nearly touch, this one's. */
export const neighbouringUnits = <T extends { geoJson: unknown }>(unit: PlanPoint[], others: T[], gap = NEIGHBOUR_GAP): T[] =>
  others.filter((other) => outlineGap(unit, readPolygon(other.geoJson)) <= gap);

/**
 * The plan image's width over its height, which the outlines' fractions do not carry: each outline
 * keeps its area in m² and the scale it was drawn at (FloorMapperView: area = shoelace(x * ratio *
 * scale, y * scale)), so the ratio is read back from them. The median of the outlines, or null.
 */
export const planAspectRatio = (geoJsons: unknown[]): number | null => {
  const ratios = geoJsons.flatMap((geoJson) => {
    const meta = (geoJson as { meta?: { calculatedAreaSqm?: unknown; calibrationSnapshot?: unknown } } | null)?.meta;
    const area = Number(meta?.calculatedAreaSqm), scale = Number(meta?.calibrationSnapshot);
    const points = readPolygon(geoJson);
    let doubled = 0;
    for (let i = 0; i < points.length; i++) {
      const a = points[i]!, b = points[(i + 1) % points.length]!;
      doubled += a.x * b.y - b.x * a.y;
    }
    const unitArea = Math.abs(doubled) / 2;
    const ratio = area / (unitArea * scale * scale);
    return Number.isFinite(ratio) && ratio > 0.2 && ratio < 5 ? [ratio] : [];
  }).sort((a, b) => a - b);
  return ratios.length ? ratios[Math.floor(ratios.length / 2)]! : null;
};
